// Shared building blocks for the Admin / SuperAdmin dashboards.
// Plain wording, no backend changes: every component tolerates missing fields.
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Metric, Text } from '@tremor/react';

// ---------- Tabs ----------

export const TabBar = ({ tabs, active, onChange }) => (
  <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-xl w-fit" role="tablist">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        role="tab"
        aria-selected={active === tab.id}
        onClick={() => onChange(tab.id)}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          active === tab.id
            ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
            : 'text-slate-500 hover:text-slate-800'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

TabBar.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

// ---------- Period selector (7 / 30 / 90 days) ----------

export const PeriodSelector = ({ value, onChange, labels }) => (
  <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
    {[7, 30, 90].map((days) => (
      <button
        key={days}
        onClick={() => onChange(days)}
        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
          value === days ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
        }`}
      >
        {labels?.[days] || `${days}d`}
      </button>
    ))}
  </div>
);

PeriodSelector.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  labels: PropTypes.object
};

// ---------- KPI card with trend vs previous period ----------

export const trendOf = (current, previous) => {
  const c = Number(current) || 0;
  const p = Number(previous) || 0;
  if (!p) return { pct: null, direction: 'flat' };
  const pct = Math.round(((c - p) / p) * 100);
  return { pct, direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
};

export const KpiCard = ({ title, value, previousValue, hint, icon, accent = 'blue', linkText, linkTo, trendLabel }) => {
  const { pct, direction } = trendOf(value, previousValue);
  const accentText = {
    blue: 'text-blue-600', green: 'text-green-600', purple: 'text-purple-600',
    amber: 'text-amber-600', red: 'text-red-600', slate: 'text-slate-600'
  }[accent] || 'text-blue-600';
  const accentBg = {
    blue: 'bg-blue-100', green: 'bg-green-100', purple: 'bg-purple-100',
    amber: 'bg-amber-100', red: 'bg-red-100', slate: 'bg-slate-100'
  }[accent] || 'bg-blue-100';

  return (
    <Card className="rounded-xl border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <Text>{title}</Text>
          <Metric>{value ?? '—'}</Metric>
          <div className="mt-1 flex items-center gap-2">
            {pct !== null ? (
              <span className={`text-xs font-semibold flex items-center gap-1 ${direction === 'up' ? 'text-green-600' : direction === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
                {direction === 'up' ? '▲' : direction === 'down' ? '▼' : '●'} {pct > 0 ? `+${pct}` : pct}%
              </span>
            ) : (
              <span className="text-xs text-slate-400">—</span>
            )}
            {trendLabel && <span className="text-xs text-slate-400">{trendLabel}</span>}
          </div>
          {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
        {icon && (
          <div className={`p-3 rounded-full ${accentBg}`}>
            <svg className={`w-6 h-6 ${accentText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
            </svg>
          </div>
        )}
      </div>
      {linkText && linkTo && (
        <Link to={linkTo} className="mt-3 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
          {linkText}
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      )}
    </Card>
  );
};

KpiCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previousValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hint: PropTypes.string,
  icon: PropTypes.string,
  accent: PropTypes.string,
  linkText: PropTypes.string,
  linkTo: PropTypes.string,
  trendLabel: PropTypes.string
};

// ---------- Chart card shell ----------

export const ChartCard = ({ title, subtitle, action, children, className = '' }) => (
  <div className={`bg-white p-6 rounded-xl border border-slate-200 shadow-sm ${className}`}>
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string
};

// ---------- Empty state (charts never crash on empty data) ----------

export const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
      <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
    <p className="text-sm text-slate-500 max-w-xs">{message}</p>
  </div>
);

EmptyState.propTypes = { message: PropTypes.string.isRequired };

// ---------- Simple table ----------

export const DataTable = ({ columns, rows, rowKey = 'id', emptyMessage }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  if (safeRows.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-100">
          {safeRows.map((row, i) => (
            <tr key={row[rowKey] ?? i} className="hover:bg-slate-50 transition-colors">
              {columns.map((col) => (
                <td key={col.key} className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                  {col.render ? col.render(row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    render: PropTypes.func
  })).isRequired,
  rows: PropTypes.array,
  rowKey: PropTypes.string,
  emptyMessage: PropTypes.string
};

// ---------- Helpers ----------

export const actionBadgeColor = (actionType = '') => {
  const a = String(actionType).toUpperCase();
  if (a.includes('LOGIN')) return 'bg-blue-100 text-blue-800';
  if (a.includes('CREATE')) return 'bg-green-100 text-green-800';
  if (a.includes('UPDATE')) return 'bg-yellow-100 text-yellow-800';
  if (a.includes('DELETE')) return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
};

export const exportToCsv = (filename, columns, rows) => {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const header = columns.map((c) => escape(c.label)).join(',');
  const safeRows = Array.isArray(rows) ? rows : [];
  const lines = safeRows.map((row) =>
    columns.map((c) => escape(c.render ? stripTags(c.render(row)) : row[c.key])).join(',')
  );
  const csv = [header, ...lines].join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Renders of table cells may be React nodes; fall back to raw value for CSV.
const stripTags = (node) => {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return node;
  if (Array.isArray(node)) return node.map(stripTags).join(' ');
  if (React.isValidElement(node)) return stripTags(node.props?.children);
  return '';
};

export const ExportButton = ({ onClick, label }) => (
  <button
    onClick={onClick}
    className="text-xs font-medium text-slate-600 hover:text-blue-600 border border-slate-200 hover:border-blue-300 rounded-md px-2.5 py-1.5 transition-colors"
  >
    {label}
  </button>
);

ExportButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired
};
